/**
 * Settings API Client
 *
 * Split from platform.ts – settings registry endpoints.
 */

import client from './client';

const api = {
  get: <T = unknown>(path: string, config?: Record<string, unknown>) => client.get<T>(`/api/platform${path}`, config),
  post: <T = unknown>(path: string, data?: unknown, config?: Record<string, unknown>) => client.post<T>(`/api/platform${path}`, data, config),
  put: <T = unknown>(path: string, data?: unknown, config?: Record<string, unknown>) => client.put<T>(`/api/platform${path}`, data, config),
  delete: <T = unknown>(path: string, config?: Record<string, unknown>) => client.delete<T>(`/api/platform${path}`, config),
};

// ============================================================
// SETTINGS INTERFACES & TYPES
// ============================================================

/**
 * Option for select/multiselect fields
 */
export interface SettingsFieldOption {
  value: string;
  label: string;
}

/**
 * Settings field types
 */
export type SettingsFieldType =
  | 'text'
  | 'email'
  | 'url'
  | 'password'
  | 'number'
  | 'boolean'
  | 'toggle'
  | 'select'
  | 'multiselect'
  | 'textarea'
  | 'color';

/**
 * Individual settings field definition
 */
export interface SettingsField {
  id: string;
  type: SettingsFieldType;
  label: string;
  description?: string;
  placeholder?: string;
  default?: string | number | boolean | string[];
  options?: SettingsFieldOption[];
  min?: number;
  max?: number;
  step?: number;
  rows?: number;
  disabled?: boolean;
  required?: boolean;
}

/**
 * Group of settings fields
 */
export interface SettingsGroup {
  id: string;
  label?: string;
  description?: string;
  fields: SettingsField[];
}

export interface SettingsPage {
  id: string;
  pluginId: string;
  sectionId: string;
  label: string;
  description?: string;
  icon?: string;
  order?: number;
  schema?: Record<string, unknown>;
  defaults?: Record<string, unknown>;
  componentUrl?: string;
  requiredPermissions?: string[];
  groups: SettingsGroup[];
}

export interface SettingsSection {
  id: string;
  pluginId: string;
  label: string;
  description?: string;
  icon?: string;
  order?: number;
  pages: SettingsPage[];
  requiredPermissions?: string[];
  registeredAt: string;
}

export interface SettingsData {
  sections: SettingsSection[];
}

export interface SettingsValidationResult {
  valid: boolean;
  errors?: Array<{ path: string; message: string }>;
}

// ============================================================
// SETTINGS FUNCTIONS
// ============================================================

export async function getSettings(): Promise<SettingsData> {
  const response = await api.get<SettingsData>('/settings');
  return response.data;
}

export async function getSettingsSections(): Promise<SettingsSection[]> {
  const response = await api.get<SettingsSection[]>('/settings/sections');
  return response.data;
}

export async function getSettingsSection(sectionId: string): Promise<SettingsSection | null> {
  const response = await api.get<SettingsSection & { error?: boolean }>(`/settings/section/${sectionId}`);
  return response.data.error ? null : response.data;
}

export async function getSettingsValues(pluginId: string, pageId: string): Promise<Record<string, unknown>> {
  const response = await api.get<{ values: Record<string, unknown> }>(`/settings/${pluginId}/${pageId}`);
  return response.data.values;
}

export async function saveSettingsValues(
  pluginId: string,
  pageId: string,
  values: Record<string, unknown>,
): Promise<SettingsValidationResult> {
  const response = await api.put<SettingsValidationResult>(`/settings/${pluginId}/${pageId}`, { values });
  return response.data;
}

export async function resetSettings(pluginId: string, pageId: string): Promise<void> {
  await api.delete(`/settings/${pluginId}/${pageId}`);
}
